import { useCallback } from 'react';

import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook to handle navigation from the customization screen.
 */
export const useCustomizationNavigation = (params: Params) => {
  const { restaurantSlug, addressId } = params;

  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const navigateToMenuScreen = useCallback(() => {
    if (addressId) {
      if (isExpoRouterEnabled) {
        router?.dismissTo({
          pathname: '/delivery/[addressId]/menu',
          params: { addressId },
        });
        return;
      }

      navigation?.navigate('MainTabs', {
        screen: 'MenuTab',
        params: { screen: 'DeliveryMenu', params: { addressId } },
      });
      return;
    }

    if (restaurantSlug) {
      if (isExpoRouterEnabled) {
        router?.dismissTo({
          pathname: '/[restaurantSlug]/menu',
          params: { restaurantSlug },
        });
        return;
      }

      navigation?.navigate('MainTabs', {
        screen: 'MenuTab',
        params: { screen: 'Menu', params: { restaurantSlug } },
      });
    }
  }, [addressId, isExpoRouterEnabled, navigation, router, restaurantSlug]);

  const navigateToPreviousScreen = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.back();
      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigateToMenuScreen();
  }, [isExpoRouterEnabled, navigation, router, navigateToMenuScreen]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    navigateToPreviousScreen,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantSlug: string | undefined;
  addressId: string | undefined;
};
